'use client'
import { Loading, Search, Send, Trash, Viewed } from '@dafiti/closet-ui/icons'
import { IconButton } from '@dafiti/closet-ui'
import { useAppLayoutContext } from '@/providers/AppLayoutProvider'
import { sanitizeSearchString, setSearchActionGA } from '@/helpers/search'
import { useHighlightMatch } from '@/hooks/useHighlightMatch'
import { useMessages } from '@/i18n/useMessages'

const ICON_CLASS_NAME =
  'closet-ui-input-search__icon-search size-3.5 text-gray-500'

interface SearchListProps {
  items: Array<string> | undefined
  type: string
  query: string
  isLeanCatalogEnabled: boolean
  isLoading?: boolean
  onClickItem: (term: string) => void
  onRemoveHistoryItem: (term: string) => void
  onFillInput: (term: string) => void
}

export function SearchList({ items, isLoading, ...props }: SearchListProps) {
  return (
    <div
      className="absolute left-0 right-0 top-full z-50 flex max-h-64 flex-col gap-4 overflow-y-auto rounded-none border-t border-gray-200 bg-white px-4 py-2 text-xs shadow-lg lg:mt-1 lg:gap-2 lg:rounded-xl lg:border lg:p-4"
      data-testid="search-list"
    >
      {isLoading ? (
        <SearchListLoading />
      ) : items?.length ? (
        <SearchListItems items={items} {...props} />
      ) : (
        <SearchListEmpty />
      )}
    </div>
  )
}

function SearchListLoading() {
  const t = useMessages()

  return (
    <div
      className="flex items-center justify-center py-2"
      data-testid="search-loading"
    >
      <Loading
        className="size-5 animate-spin text-black"
        aria-label={t.search.loadingAriaLabel}
      />
    </div>
  )
}

function SearchListEmpty() {
  const t = useMessages()

  return (
    <div className="text-left text-gray-400" data-testid="empty-state">
      {t.search.emptyState}
    </div>
  )
}

interface SearchListItemsProps {
  items: Array<string>
  type: string
  query: string
  isLeanCatalogEnabled: boolean
  onClickItem: (term: string) => void
  onRemoveHistoryItem: (term: string) => void
  onFillInput: (term: string) => void
}

function SearchListItems({
  items,
  type,
  query,
  isLeanCatalogEnabled,
  onClickItem,
  onRemoveHistoryItem,
  onFillInput,
}: SearchListItemsProps) {
  const { LinkComponent, router } = useAppLayoutContext()
  const { highlightMatch } = useHighlightMatch()
  const t = useMessages()

  const title =
    type === 'history'
      ? t.search.recentSearchesTitle
      : t.search.suggestionsTitle
  const isSearchHistory = type === 'history'

  const getSuggestionUrl = (term: string) => {
    if (isLeanCatalogEnabled) {
      return `/s/${sanitizeSearchString(term)}`
    }
    const encodedTerm = encodeURIComponent(term).replace(/%20/g, '+')
    return `/catalog/?q=${encodedTerm}&wtqs=1`
  }

  const getSearchIcon = () => {
    if (isSearchHistory) {
      return <Viewed data-testid="viewed-icon" className={ICON_CLASS_NAME} />
    }
    return <Search data-testid="search-icon" className={ICON_CLASS_NAME} />
  }

  // Navigate as early as the pointer goes down rather than waiting for the
  // click. On a real interaction the input loses focus right after pointerdown,
  // which unmounts this dropdown (and its anchor) *before* the mousedown/click
  // ever reaches it — so a click-based handler is silently dropped. pointerdown
  // is the only moment the anchor is still mounted, so we navigate there.
  // `onClick` stays as a fallback for keyboard (and any pointer the
  // pointerdown path skips); a per-node guard prevents a double navigation.
  const handleSelect = (
    e: React.MouseEvent | React.PointerEvent,
    term: string,
  ) => {
    // Let modified / non-left clicks keep the link's native behaviour
    // (e.g. cmd/ctrl+click or middle-click to open in a new tab).
    if (e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return
    }

    // Guard against the pointerdown + click pair firing twice for the same node.
    const node = e.currentTarget as HTMLElement
    if (node.dataset.cuiNavigating) {
      return
    }
    node.dataset.cuiNavigating = '1'

    e.preventDefault()
    setSearchActionGA(isSearchHistory ? 'clickRecent' : 'clickSuggestionSearch')

    if (!isSearchHistory) {
      onClickItem(term)
    }

    const url = getSuggestionUrl(term)
    if (isLeanCatalogEnabled) {
      router?.push(url)
    } else {
      window.location.assign(url)
    }
  }

  return (
    <>
      <div className="font-medium">{title}</div>

      <ul className="flex flex-col gap-3 lg:gap-2">
        {items.map((item, index) => (
          <li
            key={`${item}-${index}`}
            className="flex items-center justify-between rounded-md transition-colors duration-200 hover:bg-gray-200 focus:bg-gray-200"
          >
            <LinkComponent
              href={getSuggestionUrl(item)}
              onPointerDown={(e: React.PointerEvent) => {
                // Touch starts a scroll gesture on pointerdown — only act for
                // mouse/pen here; touch taps navigate via onClick below.
                if (e.pointerType !== 'touch') handleSelect(e, item)
              }}
              onClick={(e: React.MouseEvent) => handleSelect(e, item)}
              className="flex w-full items-center gap-2 text-left text-gray-400"
            >
              <div className="flex h-[24px] w-[24px] items-center justify-center">
                {getSearchIcon()}
              </div>

              <span>
                {isSearchHistory ? item : highlightMatch(item, query)}
              </span>
            </LinkComponent>

            {isSearchHistory ? (
              <IconButton
                size="sm"
                aria-label={t.search.removeHistoryItemAriaLabel}
                variant="subtle"
                className="flex cursor-pointer items-center justify-center hover:bg-transparent"
                onMouseDown={(e) => {
                  e.preventDefault()
                  onRemoveHistoryItem(item)
                }}
              >
                <Trash data-testid="trash-icon" className={ICON_CLASS_NAME} />
              </IconButton>
            ) : (
              <IconButton
                size="sm"
                aria-label={t.search.fillInputAriaLabel}
                variant="subtle"
                data-testid="fill-input-button"
                className="flex cursor-pointer items-center justify-center hover:bg-transparent"
                onMouseDown={(e) => {
                  e.preventDefault()
                  onFillInput(item)
                }}
              >
                <Send className={`${ICON_CLASS_NAME} rotate-y-180`} />
              </IconButton>
            )}
          </li>
        ))}
      </ul>
    </>
  )
}
